import React, { useState, useEffect } from 'react';
import { useResourceContext, IResourceContextData } from 'rm-plugin';
import { VirtualAgentControl, VirtualAgentControlClientConfig } from '@shared-ui/customer-virtual-agent-control';
import { useLocation } from 'react-router-dom-v5-compat';
import { vacEvents } from 'common/constant';
import { sendMessage } from './virtual-agent-control-helpers';
import { EGDSLayoutFlex } from '@egds/react-core/layout-flex';
import { EGDSLoader } from '@egds/react-core/loader';
import { VacOrchestrator } from '@shared-ui/retail-virtual-agent-vac-orchestrator';
import { Chat } from '@shared-ui/retail-virtual-agent-chat';
import { PubSubSignalManager } from '@shared-ui/pubsub-signal-manager';
import { SignalContextProvider } from '@shared-ui/core-signal-manager/context';
import { Experiment, ExperimentControl, ExperimentVariant, useExperiment } from '@shared-ui/experiment-context';

type StandAloneVaProps = {
  container: HTMLElement;
};

type StandAloneVAUrlParam = Pick<
  VirtualAgentControlClientConfig,
  'intentMessage' | 'conversationId' | 'participantId' | 'mode'
>;

const createContextualMessageConfig: (resource: IResourceContextData) => VirtualAgentControlClientConfig = (
  resource: IResourceContextData
) => {
  const {
    intentParams: intentMessage,
    quickLinks: { chatBackData },
  } = resource.resourceContext;
  const chatBackDataExpired = !chatBackData?.expiryUTCTime ? true : new Date() > new Date(chatBackData.expiryUTCTime);
  if (!chatBackDataExpired) {
    return {
      intentMessage: {
        intent: chatBackData.intent,
        quickLinkId: chatBackData.quickLinkId,
        ...chatBackData.payload,
      },
      mode: 'JOIN_CONVERSATION',
      conversationId: chatBackData.conversationId,
      participantId: chatBackData.participantId,
    };
  }

  if (intentMessage && Object.keys(intentMessage).length > 0) {
    return {
      intentMessage: intentMessage,
    };
  }

  return null;
};
export const StandAloneVa: React.FC<StandAloneVaProps> = ({ container }: StandAloneVaProps) => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const searchParams = new URLSearchParams(location.search);
  const queryParams: StandAloneVAUrlParam = {
    intentMessage: JSON.parse(searchParams.get('intentMessage')) || undefined,
  };
  const resource = useResourceContext();
  const contextualChatbackData = createContextualMessageConfig(resource);
  const signalManagerInstance = React.useMemo(() => new PubSubSignalManager(), []);
  const { exposure } = useExperiment('Shared_UI_VAC_Web_HelpCenter');

  useEffect(() => {
    if (exposure.bucket === 1) {
      setIsLoading(false);
    }
  }, [exposure]);
  const vacEventHandler = (event) => {
    if (event.type === vacEvents.vacConversationCreatedEvent) {
      setIsLoading(false);
      queryParams.intentMessage && sendMessage(queryParams.intentMessage);
    }
  };

  return (
    <>
      <Experiment name="Shared_UI_VAC_Web_HelpCenter">
        <ExperimentControl>
          <VirtualAgentControl
            applicationName="ecp-help-center"
            pageName="ecp-help-center"
            clientOverrides={{
              ...contextualChatbackData,
              subscribedEvents: [vacEvents.vacConversationCreatedEvent],
              eventHandler: vacEventHandler,
              enableProactiveConversation: true,
              enableAutoOpenChatWidget: true,
              embedded: true,
              containerElement: container,
            }}
            inputs={{}}
          />
        </ExperimentControl>
        <ExperimentVariant bucket={1}>
          <SignalContextProvider manager={signalManagerInstance}>
            <VacOrchestrator inputs={{ displayName: 'debugVacOrchestrator' }}>
              <Chat
                autoScroll
                inputs={{ displayName: 'debugChat' }}
                embedded
                modeOptions={{
                  mode: 'CREATE_CONVERSATION',
                  context: {
                    conversationType: 'MAIN',
                  },
                }}
              />
            </VacOrchestrator>
          </SignalContextProvider>
        </ExperimentVariant>
      </Experiment>
      {isLoading && (
        <EGDSLayoutFlex id={'stand-alone-vac-loader'} alignItems={'center'} justifyContent={'center'}>
          <EGDSLoader />
        </EGDSLayoutFlex>
      )}
    </>
  );
};
